import React, { useEffect } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { get, isEmpty } from 'lodash';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as SiteActions from '../../actions/siteActions';
import * as UiActions from '../../actions/uiActions';

import classes from './EditEntityModal.module.css';
import { squareAreaUOMTypes } from '../../data/areaTypes';

const ValidationSchema = Yup.object().shape({
    client: Yup.string()
        .required(),
    site: Yup.string()
        .required(),
    name: Yup.string()
        .required(),
    squareAreaUOM: Yup.string(),
    squareFootage: Yup.string()
        .matches(/^([1-9]{1})([0-9]{0,8})$/, 'Area must be numeric.')
        .required()
});

const EditAreaModal = ({
    close,
    clearErrors,
    fetchClientSites,
    open,
    title,
    isLoading,
    errors,
    clients,
    systemTypes,
    toggleLoading,
    ...props
}) => {

    useEffect(() => {
        async function loadSites() {
            await fetchClientSites(get(props, 'selectedClient.id', ''));
        }

        if (!isEmpty(get(props, 'selectedClient.id', '')) && open) {
            loadSites();
        }
    }, [props.selectedClient, open]);

    const formik = useFormik({
        initialValues: {
            client:        get(props, 'selectedClient.id', ''),
            site:          get(props, 'selectedSite.id', ''),
            name:          get(props, 'selectedNode.name', ''),
            squareAreaUOM: get(props, 'selectedNode.squareAreaUOM', ''),
            squareFootage: get(props, 'selectedNode.squareFootage', '')
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                companyInfo,
                updateArea,
                selectedNode
            } = props;

            const body = {
                name:          values.name,
                squareAreaUOM: values.squareAreaUOM,
                squareFootage: values.squareFootage
            };

            let isUpdated;

            clearErrors();

            if (!isLoading) {
                toggleLoading(true);
                isUpdated = await updateArea(selectedNode.id, companyInfo, body);
            }

            if (isUpdated) handleClose();
        }
    });

    const handleClose = () => {
        clearErrors();
        toggleLoading(false);
        close();
    };

    const handleClientChange = async event => {
        const { value } = event.target;

        formik.setFieldValue('site', '', false);

        formik.setTouched({
            site: false
        });

        toggleLoading(false);
        await fetchClientSites(value);

        formik.handleChange(event);
    };

    if (isEmpty(props.sites)) return null;

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Update"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/areaTreeIcon.svg" alt="edit area icon" />
                        <span>Edit Area</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Choose a unique name for this area.
                        </p>
                        <p>
                            Enter a whole number for the area and choose the unit of measure.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="client"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                Client
                            </InputLabel>
                            <Select
                                value={formik.values.client}
                                onChange={handleClientChange}
                                className={classes.select}
                                disabled={true}
                                inputProps={{
                                    name:   'client',
                                    id:     'client',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                {
                                    clients.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.client &&
                                formik.errors.client && <FormHelperText className={classes.error}>{formik.errors.client}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="site"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                Site
                            </InputLabel>
                            <Select
                                value={formik.values.site}
                                onChange={formik.handleChange}
                                disabled={true}
                                className={classes.select}
                                inputProps={{
                                    name:   'site',
                                    id:     'site',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                {
                                    props.sites.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.site &&
                                formik.errors.site && <FormHelperText className={classes.error}>{formik.errors.site}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <div className={classes.formRow}>
                            <FormControl className={classes.inputWrapper}>
                                <TextField
                                    id="squareFootage"
                                    name="squareFootage"
                                    type="text"
                                    value={formik.values.squareFootage}
                                    label="Area"
                                    onChange={formik.handleChange}
                                    InputLabelProps={{
                                        classes: {
                                            error:   classes.error,
                                            focused: classes.labelFocused,
                                            root:    classes.label
                                        }
                                    }}
                                    InputProps={{
                                        onBlur:  formik.handleBlur,
                                        classes: {
                                            error:     classes.cssErrorUnderline,
                                            underline: classes.cssUnderline,
                                            root:      classes.input
                                        }
                                    }}
                                    FormHelperTextProps={{
                                        classes: {
                                            error: classes.error
                                        }
                                    }}
                                    error={Boolean(formik.errors.squareFootage) && Boolean(formik.touched.squareFootage)}
                                    helperText={Boolean(formik.touched.squareFootage) && formik.errors.squareFootage}
                                />
                            </FormControl>
                            <FormControl className={classes.inputWrapper}>
                                <InputLabel
                                    htmlFor="squareAreaUOM"
                                    classes={{
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }}
                                    error={Boolean(formik.errors.squareAreaUOM) && Boolean(formik.touched.squareAreaUOM)}
                                >
                                    Unit Of Measure<span className={classes.required}> *</span>
                                </InputLabel>
                                <Select
                                    value={formik.values.squareAreaUOM}
                                    onChange={formik.handleChange}
                                    className={classes.select}
                                    inputProps={{
                                        name:   'squareAreaUOM',
                                        id:     'squareAreaUOM',
                                        onBlur: formik.handleBlur
                                    }}
                                    error={Boolean(formik.errors.squareAreaUOM) && Boolean(formik.touched.squareAreaUOM)}
                                >
                                    {
                                        squareAreaUOMTypes.map(uom =>
                                            <MenuItem
                                                key={uom.type}
                                                value={uom.value}
                                            >
                                                {uom.type}
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {formik.touched.squareAreaUOM &&
                                formik.errors.squareAreaUOM && <FormHelperText className={classes.error}>{formik.errors.squareAreaUOM}</FormHelperText>}
                            </FormControl>
                        </div>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

EditAreaModal.defaultProps = {
    clients:        [],
    sites:          [],
    selectedClient: {},
    selectedNode:   {},
    selectedSite:   {}
};

EditAreaModal.propTypes = {
    clients:        array,
    close:          func.isRequired,
    open:           bool.isRequired,
    sites:          array,
    selectedClient: object,
    selectedNode:   object,
    selectedSite:   object,
    title:          string.isRequired
};

const mapStateToProps = state => {
    return {
        clients:        state.architect.clientNodes,
        companyInfo:    state.companyInfo,
        errors:         state.messages.errors,
        isLoading:      state.ui.isLoading,
        selectedClient: state.client.selectedClient,
        selectedNode:   state.architect.selectedNode,
        selectedSite:   state.site.selectedSite,
        sites:          state.site.sites
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...SiteActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(EditAreaModal);
